/**
 * Workout API - Wrapper for workout functionality
 */

import { nativeBridge } from "../index"
import type {
  WorkoutCreateParams,
  WorkoutCreateResponse,
  WorkoutGetScheduledResponse,
  WorkoutScheduledEvent,
  WorkoutStartedEvent,
  WorkoutCompletedEvent,
  WorkoutCancelledEvent,
} from "../types"

export class WorkoutAPI {
  /**
   * Create and schedule a workout
   */
  async create(workoutId: string, location?: "indoor" | "outdoor"): Promise<WorkoutCreateResponse> {
    return nativeBridge.request<WorkoutCreateParams, WorkoutCreateResponse>("workout.create", { workoutId, location })
  }

  /**
   * Get all scheduled workouts
   */
  async getScheduled(): Promise<WorkoutGetScheduledResponse> {
    return nativeBridge.request<void, WorkoutGetScheduledResponse>("workout.getScheduled")
  }

  /**
   * Cancel a scheduled workout
   */
  async cancel(workoutId: string): Promise<void> {
    return nativeBridge.request("workout.cancel", { workoutId })
  }

  /**
   * Subscribe to workout scheduled events
   */
  async onScheduled(callback: (event: WorkoutScheduledEvent) => void): Promise<() => Promise<void>> {
    const subscriptionId = await nativeBridge.subscribe<WorkoutScheduledEvent>("workout.scheduled", callback)

    return async () => {
      await nativeBridge.unsubscribe(subscriptionId)
    }
  }

  /**
   * Subscribe to workout started events
   */
  async onStarted(callback: (event: WorkoutStartedEvent) => void): Promise<() => Promise<void>> {
    const subscriptionId = await nativeBridge.subscribe<WorkoutStartedEvent>("workout.started", callback)

    return async () => {
      await nativeBridge.unsubscribe(subscriptionId)
    }
  }

  /**
   * Subscribe to workout completed events
   */
  async onCompleted(callback: (event: WorkoutCompletedEvent) => void): Promise<() => Promise<void>> {
    const subscriptionId = await nativeBridge.subscribe<WorkoutCompletedEvent>("workout.completed", callback)

    return async () => {
      await nativeBridge.unsubscribe(subscriptionId)
    }
  }

  /**
   * Subscribe to workout cancelled events
   */
  async onCancelled(callback: (event: WorkoutCancelledEvent) => void): Promise<() => Promise<void>> {
    const subscriptionId = await nativeBridge.subscribe<WorkoutCancelledEvent>("workout.cancelled", callback)

    return async () => {
      await nativeBridge.unsubscribe(subscriptionId)
    }
  }
}

// Export singleton instance
export const workoutAPI = new WorkoutAPI()
